import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import useWindowDimensions from "../../common/useWindowDimensions";
import { checkedCircleIconSmall } from "../../common/checkedCircleIcon";
import { cancelIconRedSmall } from "../../common/cancelIconRed";
import { formatSiteTimestamp } from "./ActionLogsUtils/formatSiteTimestamp";

// Consolidated schedule history (ED-14): the API returns one entry per save,
// each listing only the days that actually changed. We render one row per
// changed day, so a single edit reads as a small group instead of the raw
// replace-the-week churn (~15 rows per save).

interface ScheduleState {
  start_time: string | null;
  end_time: string | null;
  always_on: boolean;
  enabled: boolean;
  override_start_date: string | null;
  override_end_date: string | null;
}

interface ScheduleChange {
  day_of_week: string;
  day_of_week_str: string;
  is_override: boolean;
  flex_schedule: number | null;
  change_type: "added" | "changed" | "removed";
  before: ScheduleState | null;
  after: ScheduleState | null;
}

interface ConsolidatedEntry {
  history_date: string;
  username: string | null;
  changes: ScheduleChange[];
}

const HEADERS = [
  "Date",
  "Action",
  "Days",
  "Start Time",
  "End Time",
  "Flex Schedule",
  "User",
];

// Only the save timestamp is converted; start/end times are the schedule's
// own wall-clock values and render as stored.
function formatDateString(
  dateString: string,
  siteTimezone: string | null
): string {
  return formatSiteTimestamp(new Date(dateString), siteTimezone);
}

const ACTION_LABELS: Record<ScheduleChange["change_type"], string> = {
  added: "Added",
  changed: "Changed",
  removed: "Removed",
};

function stateTime(
  state: ScheduleState | null,
  field: "start_time" | "end_time"
): string {
  if (state == null) return "-";
  if (state.always_on) return "Always On";
  return state[field] ?? "-";
}

// added → the new value; removed → the old value; changed → "old → new"
function timeCell(
  change: ScheduleChange,
  field: "start_time" | "end_time"
): string {
  if (change.change_type === "changed") {
    const before = stateTime(change.before, field);
    const after = stateTime(change.after, field);
    return before === after ? after : `${before} → ${after}`;
  }
  return stateTime(
    change.change_type === "removed" ? change.before : change.after,
    field
  );
}

interface FlatRow {
  key: string;
  entry: ConsolidatedEntry;
  change: ScheduleChange;
}

export const ScheduleActionLogsTable = ({
  scheduleHistoryResults,
  siteTimezone = null,
}: {
  scheduleHistoryResults: ConsolidatedEntry[];
  siteTimezone?: string | null;
}) => {
  const { height, width } = useWindowDimensions();
  const [calculatedHeight, setCalculatedHeight] = React.useState(0);
  const [calculatedWidth, setCalculatedWidth] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const rows: FlatRow[] = React.useMemo(
    () =>
      scheduleHistoryResults.flatMap((entry, entryIndex) =>
        (entry.changes ?? []).map((change, changeIndex) => ({
          key: `${entry.history_date}-${entryIndex}-${changeIndex}`,
          entry,
          change,
        }))
      ),
    [scheduleHistoryResults]
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pageClamped = Math.min(
    page,
    Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1)
  );
  const visibleRows = React.useMemo(
    () =>
      rows.slice(
        pageClamped * rowsPerPage,
        pageClamped * rowsPerPage + rowsPerPage
      ),
    [rows, pageClamped, rowsPerPage]
  );

  React.useEffect(() => {
    setCalculatedHeight(height - 205);
    setCalculatedWidth(width - 150);
  }, [height, width]);

  React.useEffect(() => {
    if (page > pageClamped) setPage(pageClamped);
  }, [pageClamped, page]);

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer
        sx={{ maxHeight: calculatedHeight, minWidth: calculatedWidth }}
      >
        <Table
          sx={{ fontFamily: "Mulish" }}
          aria-label="schedule action logs table"
          stickyHeader
        >
          <TableHead>
            <TableRow>
              {HEADERS.map((label) => (
                <TableCell
                  key={label}
                  sx={{
                    fontSize: "15px",
                    fontFamily: "Mulish",
                    fontWeight: "Bold",
                  }}
                  align="left"
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={HEADERS.length}
                  align="center"
                  sx={{ fontFamily: "Mulish" }}
                >
                  No schedule action logs to display.
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map(({ key, entry, change }) => (
                <TableRow key={key}>
                  <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                    {formatDateString(entry.history_date, siteTimezone)}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                    {ACTION_LABELS[change.change_type] ?? change.change_type}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                    {change.day_of_week_str}
                    {change.is_override ? " (override)" : ""}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                    {timeCell(change, "start_time")}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                    {timeCell(change, "end_time")}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                    {change.flex_schedule != null
                      ? checkedCircleIconSmall
                      : cancelIconRedSmall}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                    {entry.username ?? "-"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={pageClamped}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};
